import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Field,
  Label,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  Textarea,
} from "@a7y/tailwind-catalyst";
import {
  ORG_ROLES,
  ORG_ROLE_LABELS,
  SUPPORT_ACCESS_DEFAULT_TTL_DAYS,
  SUPPORT_ACCESS_MAX_TTL_DAYS,
  type AdminSuperadminUser,
  type AdminSupportAccessEntry,
  type OrgRole,
} from "@a7y/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LifeBuoy, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { ROLE_COLORS } from "./constants";
import { Section } from "./shared";

export interface SupportAccessTabProps {
  orgId: string;
}

const ROLE_OPTIONS: readonly OrgRole[] = ORG_ROLES;

const TTL_OPTIONS_DAYS = [1, 3, SUPPORT_ACCESS_DEFAULT_TTL_DAYS, 14, SUPPORT_ACCESS_MAX_TTL_DAYS];

function ttlOptionLabel(days: number): string {
  if (days === SUPPORT_ACCESS_MAX_TTL_DAYS) return `${days} days (maximum)`;
  return days === 1 ? "1 day" : `${days} days`;
}

export function SupportAccessTab({ orgId }: SupportAccessTabProps) {
  const [grantOpen, setGrantOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<AdminSupportAccessEntry | null>(null);

  const { data, isLoading, error } = useQuery<{ data: AdminSupportAccessEntry[] }>({
    queryKey: ["support-access", orgId],
    queryFn: () => api.listSupportAccess(orgId),
    enabled: !!orgId,
  });

  const grants = data?.data ?? [];

  return (
    <>
      <Section
        title={`Support Access (${grants.length})`}
        icon={<LifeBuoy className="h-5 w-5" />}
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <Text className="text-sm text-zinc-500 dark:text-zinc-400">
            Active support-access grants let Apptonomy staff browse this organization
            without being members. Each grant is auditable, expires automatically, and
            can be revoked at any time.
          </Text>
          <Button color="blue" onClick={() => setGrantOpen(true)}>
            <Plus data-slot="icon" />
            Grant access
          </Button>
        </div>

        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-300 dark:border-zinc-600 border-t-blue-600" />
          </div>
        ) : error ? (
          <Text className="text-sm text-red-600">
            Failed to load support access: {(error as Error)?.message}
          </Text>
        ) : (
          <Table className="[--gutter:--spacing(6)] sm:[--gutter:--spacing(8)]">
            <TableHead>
              <TableRow>
                <TableHeader>User</TableHeader>
                <TableHeader>Role</TableHeader>
                <TableHeader>Reason</TableHeader>
                <TableHeader>Granted by</TableHeader>
                <TableHeader>Granted at</TableHeader>
                <TableHeader>Last accessed</TableHeader>
                <TableHeader>Expires</TableHeader>
                <TableHeader className="text-right">Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {grants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center">
                    <div className="flex flex-col items-center gap-1 py-4">
                      <Text className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                        No active support access grants
                      </Text>
                      <Text className="text-xs text-zinc-500">
                        Grant access to a superadmin to let them browse this org without being a member.
                      </Text>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                grants.map((grant) => (
                  <TableRow key={grant.id}>
                    <TableCell>
                      <div>
                        <Text className="font-medium">
                          {grant.user.name || grant.user.email || (
                            <span className="font-mono text-xs text-zinc-500">{grant.userId}</span>
                          )}
                        </Text>
                        {grant.user.email && (
                          <Text className="text-zinc-500">{grant.user.email}</Text>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge color={ROLE_COLORS[grant.role] ?? "zinc"}>
                        {grant.role.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <Text
                        className="line-clamp-2 text-sm text-zinc-700 dark:text-zinc-300"
                        title={grant.reason}
                      >
                        {grant.reason || (
                          <span className="text-zinc-400 italic">No reason</span>
                        )}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <div>
                        <Text className="font-medium">
                          {grant.grantedByName || grant.grantedByEmail || (
                            <span className="font-mono text-xs text-zinc-500">{grant.grantedBy}</span>
                          )}
                        </Text>
                        {grant.grantedByName && grant.grantedByEmail && (
                          <Text className="text-zinc-500">{grant.grantedByEmail}</Text>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-zinc-500">
                      {formatDate(grant.grantedAt)}
                    </TableCell>
                    <TableCell className="text-zinc-500">
                      {grant.lastAccessedAt ? formatDate(grant.lastAccessedAt) : "Never"}
                    </TableCell>
                    <TableCell className="text-zinc-500">
                      {formatDate(grant.expiresAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        plain
                        onClick={() => setRevokeTarget(grant)}
                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Revoke
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Section>

      {grantOpen && (
        <GrantSupportAccessDialog
          orgId={orgId}
          existingUserIds={grants.map((g) => g.userId)}
          onClose={() => setGrantOpen(false)}
        />
      )}

      {revokeTarget && (
        <RevokeSupportAccessDialog
          orgId={orgId}
          grant={revokeTarget}
          onClose={() => setRevokeTarget(null)}
        />
      )}
    </>
  );
}

function GrantSupportAccessDialog({
  orgId,
  existingUserIds,
  onClose,
}: {
  orgId: string;
  existingUserIds: string[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState<string>("");
  // A7Y-5057: the least-privileged role is the default grant.
  const [role, setRole] = useState<OrgRole>("standard");
  const [ttlDays, setTtlDays] = useState<number>(SUPPORT_ACCESS_DEFAULT_TTL_DAYS);
  const [reason, setReason] = useState<string>("");
  const [reasonTouched, setReasonTouched] = useState<boolean>(false);

  const superadminsQuery = useQuery<{ data: AdminSuperadminUser[] }>({
    queryKey: ["superadmins"],
    queryFn: () => api.listSuperadmins(),
  });

  const grantMutation = useMutation({
    mutationFn: () =>
      api.grantSupportAccess(orgId, { userId, role, reason: reason.trim(), ttlDays }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-access", orgId] });
      onClose();
    },
  });

  const availableUsers = useMemo(() => {
    const all = superadminsQuery.data?.data ?? [];
    const existing = new Set(existingUserIds);
    return all.filter((u) => !existing.has(u.id));
  }, [superadminsQuery.data, existingUserIds]);

  const trimmedReason = reason.trim();
  const reasonError =
    trimmedReason.length === 0
      ? "Reason is required."
      : trimmedReason.length < 5
        ? "Reason must be at least 5 characters."
        : trimmedReason.length > 500
          ? "Reason must be at most 500 characters."
          : null;
  const reasonValid = reasonError === null;

  const canSubmit =
    userId.length > 0 &&
    role.length > 0 &&
    reasonValid &&
    !grantMutation.isPending;

  return (
    <Dialog open onClose={onClose} size="md">
      <DialogTitle>Grant support access</DialogTitle>
      <DialogBody>
        <Text className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
          Pick a superadmin and the role they should have inside this organization.
          Granted access is auditable, expires automatically, and can be revoked at any time.
        </Text>

        <Field>
          <Label>Superadmin</Label>
          <Select
            value={userId}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setUserId(e.target.value)}
            disabled={superadminsQuery.isLoading}
          >
            <option value="">
              {superadminsQuery.isLoading
                ? "Loading superadmins..."
                : availableUsers.length === 0
                  ? "No eligible superadmins"
                  : "Select a superadmin"}
            </option>
            {availableUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name ? `${u.name} — ${u.email ?? u.id}` : (u.email ?? u.id)}
              </option>
            ))}
          </Select>
        </Field>

        <Field className="mt-4">
          <Label>Role</Label>
          <Select
            value={role}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setRole(e.target.value as OrgRole)}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {ORG_ROLE_LABELS[r]}
              </option>
            ))}
          </Select>
        </Field>

        <Field className="mt-4">
          <Label>Duration</Label>
          <Select
            value={String(ttlDays)}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setTtlDays(Number(e.target.value))
            }
          >
            {TTL_OPTIONS_DAYS.map((days) => (
              <option key={days} value={days}>
                {ttlOptionLabel(days)}
              </option>
            ))}
          </Select>
          <Text className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
            Access ends automatically after this period.
          </Text>
        </Field>

        <Field className="mt-4">
          <Label>Reason</Label>
          <Textarea
            value={reason}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
            onBlur={() => setReasonTouched(true)}
            rows={3}
            maxLength={500}
            placeholder="Why does this superadmin need access? (5-500 chars, e.g. 'Investigating customer-reported audit credibility regression in ticket #4821.')"
            aria-invalid={reasonTouched && !reasonValid}
            aria-describedby="support-access-reason-help"
          />
          <Text
            id="support-access-reason-help"
            className={`mt-1 text-xs ${
              reasonTouched && reasonError
                ? "text-red-600"
                : "text-zinc-500 dark:text-zinc-400"
            }`}
          >
            {reasonTouched && reasonError
              ? reasonError
              : `${trimmedReason.length}/500 characters — captured on the grant for SOC2-friendly audit.`}
          </Text>
        </Field>

        {superadminsQuery.isError && (
          <Text className="mt-3 text-xs text-red-600">
            Failed to load superadmins: {(superadminsQuery.error as Error)?.message}
          </Text>
        )}
        {grantMutation.isError && (
          <Text className="mt-3 text-xs text-red-600">
            Failed to grant access: {(grantMutation.error as Error)?.message}
          </Text>
        )}
      </DialogBody>
      <DialogActions>
        <Button outline onClick={onClose} disabled={grantMutation.isPending}>
          Cancel
        </Button>
        <Button
          color="blue"
          disabled={!canSubmit}
          onClick={() => grantMutation.mutate()}
        >
          {grantMutation.isPending ? "Granting..." : "Grant access"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RevokeSupportAccessDialog({
  orgId,
  grant,
  onClose,
}: {
  orgId: string;
  grant: AdminSupportAccessEntry;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const revokeMutation = useMutation({
    mutationFn: () => api.revokeSupportAccess(orgId, grant.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-access", orgId] });
      onClose();
    },
  });

  const userLabel = grant.user.name || grant.user.email || grant.userId;

  return (
    <Dialog open onClose={onClose} size="sm">
      <DialogTitle>Revoke support access</DialogTitle>
      <DialogBody>
        <Text className="text-sm text-zinc-700 dark:text-zinc-300">
          Revoke <strong>{userLabel}</strong>'s <strong>{grant.role}</strong> support access?
        </Text>
        <Text className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
          They will lose the ability to browse this organization on their next request.
          This cannot be undone — you'll need to grant a new access entry to restore it.
        </Text>
        {revokeMutation.isError && (
          <Text className="mt-3 text-xs text-red-600">
            Failed to revoke: {(revokeMutation.error as Error)?.message}
          </Text>
        )}
      </DialogBody>
      <DialogActions>
        <Button outline onClick={onClose} disabled={revokeMutation.isPending}>
          Cancel
        </Button>
        <Button
          color="red"
          disabled={revokeMutation.isPending}
          onClick={() => revokeMutation.mutate()}
        >
          {revokeMutation.isPending ? "Revoking..." : "Revoke access"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
